import { useNavigate, useSearchParams } from 'react-router-dom';
import type { Device, DeviceKind, DeviceStatus } from '@iot-dashboard/shared';
import { CAMERA_KINDS, KIND_LABELS, emptyStatusCounts } from '@iot-dashboard/shared';
import { useDevices, useSites } from '../../api/hooks';
import { DataTable, type Column } from '../../components/DataTable';
import { KindIcon } from '../../components/KindIcon';
import { Panel } from '../../components/Panel';
import { StatusDot } from '../../components/StatusDot';
import { StatusSummary } from '../../components/StatusSummary';
import { ErrorState, Loading } from '../../components/States';
import { TimeAgo } from '../../components/TimeAgo';
import { PageHeader } from '../../layout/PageHeader';
import { useTenantContext } from '../../layout/TenantLayout';

const DETECTION_LABELS: Record<string, string> = {
  person: 'People',
  vehicle: 'Vehicles',
  animal: 'Animals',
  package: 'Packages',
  licensePlate: 'Plates',
  face: 'Faces',
};

export function detectionSummary(d: Device): string {
  const types = d.camera?.smartDetectTypes ?? [];
  if (types.length === 0) return '—';
  return types.map((t) => DETECTION_LABELS[t] ?? t).join(', ');
}

/** Mic, speaker and HDR, the hardware differences worth seeing in a list. */
export function featureSummary(d: Device): string {
  const c = d.camera;
  if (!c) return '—';
  const parts: string[] = [];
  if (c.hasMic) parts.push(c.micEnabled === false ? 'Mic (off)' : 'Mic');
  if (c.hasSpeaker) parts.push('Speaker');
  if (c.hasHdr) parts.push('HDR');
  if (c.hasPackageCamera) parts.push('Package lens');
  return parts.length ? parts.join(' · ') : '—';
}

export function CamerasPage() {
  const { tenantId, base } = useTenantContext();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const filters = {
    deviceClass: 'camera' as const,
    siteId: params.get('site') ?? undefined,
    kind: (params.get('kind') as DeviceKind | null) ?? undefined,
    status: (params.get('status') as DeviceStatus | null) ?? undefined,
    q: params.get('q') ?? undefined,
  };
  const cameras = useDevices(tenantId, filters);
  const all = useDevices(tenantId, { deviceClass: 'camera', siteId: filters.siteId });
  const sites = useSites(tenantId);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const counts = (all.data ?? []).reduce((c, d) => ((c[d.status] += 1), c), emptyStatusCounts());
  const siteName = (id: string) => sites.data?.find((s) => s.siteId === id)?.name ?? id;

  const columns: Column<Device>[] = [
    { key: 'status', header: 'Status', sortValue: (d) => d.status, render: (d) => <StatusDot status={d.status} /> },
    {
      key: 'name',
      header: 'Name',
      primary: true,
      sortValue: (d) => d.name,
      render: (d) => (
        <span className="row">
          <KindIcon kind={d.kind} />
          <span className="cell-primary">{d.name}</span>
        </span>
      ),
    },
    { key: 'kind', header: 'Type', sortValue: (d) => d.kind, render: (d) => KIND_LABELS[d.kind] },
    { key: 'site', header: 'Site', sortValue: (d) => siteName(d.siteId), render: (d) => siteName(d.siteId) },
    { key: 'detections', header: 'Detects', render: (d) => <span className="small">{detectionSummary(d)}</span> },
    { key: 'features', header: 'Audio & video', render: (d) => <span className="small">{featureSummary(d)}</span> },
    { key: 'seen', header: 'Last seen', sortValue: (d) => d.lastSeenAt ?? '', render: (d) => <TimeAgo iso={d.lastSeenAt} /> },
    { key: 'tags', header: 'Tags', render: (d) => d.tags.map((t) => <span key={t} className="tag">{t}</span>) },
  ];

  if (cameras.isError) return <ErrorState error={cameras.error} retry={() => cameras.refetch()} />;

  return (
    <>
      <PageHeader
        title="Cameras"
        subtitle="Cameras and doorbells reported by this tenant's consoles."
      />
      <Panel>
        <StatusSummary counts={counts} active={filters.status ?? ''} onSelect={(s) => setParam('status', s)} />
      </Panel>
      <Panel
        flush
        title={
          <div className="filters">
            <input
              className="input"
              placeholder="Search name, MAC, tag"
              value={filters.q ?? ''}
              onChange={(e) => setParam('q', e.target.value)}
              aria-label="Search cameras"
            />
            <select
              className="select"
              value={filters.kind ?? ''}
              onChange={(e) => setParam('kind', e.target.value)}
              aria-label="Filter by type"
            >
              <option value="">All types</option>
              {CAMERA_KINDS.map((k) => (
                <option key={k} value={k}>{KIND_LABELS[k]}</option>
              ))}
            </select>
            <select
              className="select"
              value={filters.siteId ?? ''}
              onChange={(e) => setParam('site', e.target.value)}
              aria-label="Filter by site"
            >
              <option value="">All sites</option>
              {(sites.data ?? []).map((s) => (
                <option key={s.siteId} value={s.siteId}>{s.name}</option>
              ))}
            </select>
            {filters.q || filters.kind || filters.status || filters.siteId ? (
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => setParams({}, { replace: true })}>
                Clear filters
              </button>
            ) : null}
          </div>
        }
        actions={
          <span className="small muted">
            {cameras.data?.length ?? 0} camera{cameras.data?.length === 1 ? '' : 's'}
          </span>
        }
      >
        {cameras.isPending ? (
          <Loading label="Loading cameras…" />
        ) : (
          <DataTable
            columns={columns}
            rows={cameras.data}
            rowKey={(d) => d.deviceId}
            onRowClick={(d) => navigate(`${base}/cameras/${encodeURIComponent(d.deviceId)}`)}
            defaultSort={{ key: 'name', dir: 'asc' }}
            refetching={cameras.isFetching}
            empty={
              <div className="empty">
                <h3>No cameras match</h3>
                <p>Cameras appear after the integration has synced a console that has cameras.</p>
              </div>
            }
          />
        )}
      </Panel>
    </>
  );
}
